type WindowEventName = keyof WindowEventMap
type WindowEventHandler<K extends WindowEventName> = (event: WindowEventMap[K]) => void
type StoredHandler = (event: Event) => void

class CarouselWindowListenerManager {
  private listeners = new Map<WindowEventName, Set<StoredHandler>>()
  private wrappers = new Map<WindowEventName, EventListener>()

  register<K extends WindowEventName>(eventName: K, handler: WindowEventHandler<K>) {
    const storedHandler = handler as unknown as StoredHandler
    let handlers = this.listeners.get(eventName)

    if (!handlers) {
      handlers = new Set()
      this.listeners.set(eventName, handlers)
    }

    if (!handlers.has(storedHandler)) {
      handlers.add(storedHandler)
    }

    this.ensureListener(eventName)
  }

  unregister<K extends WindowEventName>(eventName: K, handler: WindowEventHandler<K>) {
    const handlers = this.listeners.get(eventName)

    if (!handlers) return

    handlers.delete(handler as unknown as StoredHandler)

    if (handlers.size === 0) {
      this.removeListener(eventName)
      this.listeners.delete(eventName)
    }
  }

  private ensureListener(eventName: WindowEventName) {
    if (this.wrappers.has(eventName)) return

    const dispatcher: EventListener = (event: Event) => {
      const handlers = this.listeners.get(eventName)

      if (!handlers) return

      handlers.forEach((stored) => {
        stored(event)
      })
    }

    this.wrappers.set(eventName, dispatcher)
    window.addEventListener(eventName, dispatcher)
  }

  private removeListener(eventName: WindowEventName) {
    const dispatcher = this.wrappers.get(eventName)

    if (!dispatcher) return

    window.removeEventListener(eventName, dispatcher)
    this.wrappers.delete(eventName)
  }
}

// Use a global key to ensure only one instance of the carousel window listener manager is created
const globalKey = '__nutra_carousel_window_listener_manager__'
const globalAny = globalThis as any

if (!globalAny[globalKey]) {
  globalAny[globalKey] = new CarouselWindowListenerManager()
}

export const carouselWindowListenerManager = globalAny[globalKey] as CarouselWindowListenerManager
